import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type {
  BankConnectionData,
  BankAccountData,
  BankTransactionData,
  JournalAttachmentData,
} from '@/types/api';

const CONNECTIONS_KEY = ['bank', 'connections'] as const;
const POSTABLE_ACCOUNTS_KEY = ['bank', 'postable-accounts'] as const;
const TRANSACTIONS_KEY = ['bank', 'transactions'] as const;

export type BankTransactionFilters = {
  status?: string;
  bank_account_id?: number;
  q?: string;
  from?: string;
  to?: string;
  sort?: string;
  page?: number;
};

export type SyncSummary = { added: number; modified: number; removed: number };

export type PostableAccount = {
  id: number;
  code: string;
  name: string;
  /** 'asset' or 'liability'; matched against BankAccountData.ledger_account_types. */
  type: string;
  label: string;
};

export function useBankConnections() {
  return useQuery({
    queryKey: CONNECTIONS_KEY,
    queryFn: async (): Promise<BankConnectionData[]> => {
      const { data } = await api.get<BankConnectionData[]>('/v1/bank/connections');
      return data;
    },
  });
}

/**
 * Mint a Plaid Link token. react-plaid-link's usePlaidLink() takes this token
 * and pops the institution-picker UI. On Link success we call
 * useCreateBankConnection with the returned public_token.
 */
export function useCreateLinkToken() {
  return useMutation({
    mutationFn: async (): Promise<string> => {
      await primeCsrf();
      const { data } = await api.post<{ link_token: string }>('/v1/bank/link-token');
      return data.link_token;
    },
  });
}

export function useCreateBankConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (publicToken: string): Promise<BankConnectionData> => {
      await primeCsrf();
      const { data } = await api.post<BankConnectionData>('/v1/bank/connections', {
        public_token: publicToken,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  });
}

export function useDeleteBankConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/bank/connections/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  });
}

/**
 * Change where an external account posts, or archive it. Bank accounts are
 * never deleted: their transactions are already in the ledger and would be
 * stranded. Archiving takes the account out of the transfer picker and out of
 * the day-to-day view.
 */
export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...patch
    }: {
      id: number;
      posted_to_account_id?: number | null;
      archived?: boolean;
    }): Promise<BankAccountData> => {
      await primeCsrf();
      const { data } = await api.patch<BankAccountData>(`/v1/bank/accounts/${id}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONNECTIONS_KEY }),
  });
}

export function usePostableAccounts() {
  return useQuery({
    queryKey: POSTABLE_ACCOUNTS_KEY,
    queryFn: async (): Promise<PostableAccount[]> => {
      const { data } = await api.get<{ data: PostableAccount[] }>('/v1/bank/postable-accounts');
      return data.data;
    },
  });
}

/** Pull the latest transactions for one connection from Plaid. */
export function useSyncConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: number): Promise<SyncSummary> => {
      await primeCsrf();
      const { data } = await api.post<SyncSummary>(`/v1/bank/connections/${connectionId}/sync`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY });
      qc.invalidateQueries({ queryKey: CONNECTIONS_KEY });
    },
  });
}

export function useBankTransactions(filters: BankTransactionFilters = {}) {
  return useQuery({
    queryKey: [...TRANSACTIONS_KEY, filters],
    queryFn: async (): Promise<Paginated<BankTransactionData>> => {
      const { data } = await api.get<Paginated<BankTransactionData>>('/v1/bank/transactions', {
        params: filters,
      });
      return data;
    },
  });
}

export function usePostTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      account_id,
    }: {
      id: number;
      account_id: number;
    }): Promise<BankTransactionData> => {
      await primeCsrf();
      const { data } = await api.post<BankTransactionData>(`/v1/bank/transactions/${id}/post`, {
        account_id,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}

/**
 * Record a transaction as an inter-account transfer against another linked bank
 * account. The server posts a single balanced contra entry and reconciles the
 * opposite leg, so both legs of the movement change — invalidate the list.
 */
export function useTransferTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      counterparty_bank_account_id,
    }: {
      id: number;
      counterparty_bank_account_id: number;
    }): Promise<BankTransactionData> => {
      await primeCsrf();
      const { data } = await api.post<BankTransactionData>(
        `/v1/bank/transactions/${id}/transfer`,
        { counterparty_bank_account_id },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}

export type RefundCandidate = {
  id: number;
  label: string;
  amount_cents: number;
  txn_date: string;
  bank_account_name: string | null;
};

/**
 * Posted purchases this inflow could be a refund of. Only fetched while the row
 * is actually in refund mode (enabled), to avoid a request per transaction.
 */
export function useRefundCandidates(txnId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...TRANSACTIONS_KEY, 'refund-candidates', txnId],
    queryFn: async (): Promise<RefundCandidate[]> => {
      const { data } = await api.get<{ data: RefundCandidate[] }>(
        `/v1/bank/transactions/${txnId}/refund-candidates`,
      );
      return data.data;
    },
    enabled,
  });
}

/**
 * Record an inflow as a refund of an earlier purchase. The server credits the
 * original's expense category rather than booking income.
 */
export function useRefundTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      original_bank_transaction_id,
    }: {
      id: number;
      original_bank_transaction_id: number;
    }): Promise<BankTransactionData> => {
      await primeCsrf();
      const { data } = await api.post<BankTransactionData>(`/v1/bank/transactions/${id}/refund`, {
        original_bank_transaction_id,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}

export function useIgnoreTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<BankTransactionData> => {
      await primeCsrf();
      const { data } = await api.post<BankTransactionData>(`/v1/bank/transactions/${id}/ignore`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}

export function useUnpostTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<BankTransactionData> => {
      await primeCsrf();
      const { data } = await api.post<BankTransactionData>(`/v1/bank/transactions/${id}/unpost`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}

export function useUploadTransactionAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      txnId,
      file,
    }: {
      txnId: number;
      file: File;
    }): Promise<JournalAttachmentData> => {
      await primeCsrf();
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post<JournalAttachmentData>(
        `/v1/bank/transactions/${txnId}/attachments`,
        form,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}

export function useDeleteTransactionAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ txnId, mediaId }: { txnId: number; mediaId: number }): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/bank/transactions/${txnId}/attachments/${mediaId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
  });
}
